import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import {
  ArrowRight, ArrowUpRight, Shield, Globe, Lock,
  CheckCircle, Wifi, KeyRound, Trophy, Award,
  Gauge, Smartphone, MapPin,
} from 'lucide-react';
import { Button } from './ui/button';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { openBilling } from '../lib/referral';
import { smoothScrollToWithOffset } from '../lib/scroll';
import { locations as locationsEn, locationsRu } from '../data/mock';

/**
 * VPN service marketing page. Mirrors the look of our location pages but
 * built around the VPN product line: WireGuard / OpenVPN / AmneziaVPN /
 * XRay VLESS, three plans (Solo / Family / Office), 13 datacenter
 * locations, and the differentiator vs commercial public VPNs (NordVPN,
 * CyberGhost, etc.) — your own dedicated IP that nobody else uses.
 */

// All copy lives inline so we don't have to bloat translations.js with
// hundreds of keys for a single product page.
const COPY = {
  en: {
    eyebrow: 'Personal VPN — your own IP, your rules',
    h1: 'A private VPN you actually own',
    h1Sub: 'WireGuard, OpenVPN, AmneziaVPN and XRay VLESS — running on a dedicated IP that nobody but you uses.',
    cta: 'Get your VPN',
    cta2: 'See plans',
    statUptime: 'Uptime SLA',
    statTraffic: 'Unlimited traffic',
    statIP: 'Dedicated IP',
    statLocations: '13 locations',

    // Why us vs commercial VPNs
    whyTitle: 'Why a private VPN beats a commercial one',
    whyLead: 'Commercial VPN providers (NordVPN, CyberGhost, PureVPN, ProtonVPN, Surfshark and the rest) sell access to a shared pool — thousands of users behind the same handful of IP addresses. That works for "hide my city from this website", but it has trade-offs you don\'t see in the ads.',
    whyTable: [
      ['Your IP address', 'Shared with thousands of others — easily flagged as "VPN" everywhere', 'Yours alone — looks like any normal residential or datacenter IP'],
      ['IP reputation', 'Pre-burned by abuse from random users, often blocked by Netflix, banks, captcha walls', 'Clean — only you ever use it'],
      ['Privacy from the provider', '"No-logs" claims you have to trust, and have been broken before', 'You own the box. We see metadata only — no inspection of contents'],
      ['Configuration', 'Locked to their app. Can\'t add a peer, can\'t tweak routing.', 'Standard WireGuard / OpenVPN / etc. Your own .conf file. Use any client.'],
      ['Number of devices', 'Capped per subscription tier', 'Up to 25 devices on the Office plan, plus add-on capacity'],
      ['Use as bastion / port forwarding', 'Almost never possible', 'Yes — it\'s your own server'],
    ],
    whyVendors: ['NordVPN', 'CyberGhost', 'PureVPN', 'ProtonVPN', 'Surfshark', 'ExpressVPN'],

    // Locations
    locTitle: 'Available in all our datacenter locations',
    locSub: 'Pick the country your traffic should appear from. Same network, same speed, same uptime as our VPS plans.',

    // Protocols (tabs)
    protoTitle: 'Pick your VPN protocol',
    protoSub: 'WireGuard is the right answer for 95% of cases — fast, modern, in the Linux kernel. The other protocols are there when you need them.',
    protocols: [
      {
        key: 'wireguard',
        name: 'WireGuard',
        badge: 'Default • Recommended',
        tagline: 'The modern VPN protocol — fast, lean, in the Linux kernel.',
        bullets: [
          'Tiny codebase (~4 000 lines vs ~600 000 for OpenVPN), formally reviewed.',
          'In the mainline Linux kernel since 5.6 — no userspace daemon, no overhead.',
          'On the same hardware: 800–950 Mbps throughput vs 150–300 Mbps for OpenVPN.',
          'Reconnects in <100 ms — designed for switching from Wi-Fi to mobile.',
          'Config fits in a QR code — scan with the WireGuard app and you\'re online.',
          'Crypto: ChaCha20-Poly1305 + Curve25519, no choices to mess up.',
        ],
        bestFor: 'Daily use on phone, laptop, router. Default choice.',
      },
      {
        key: 'openvpn',
        name: 'OpenVPN',
        badge: 'Legacy support',
        tagline: 'The classic, broadly compatible VPN. Slower than WireGuard but supported by anything from 2005.',
        bullets: [
          'Userspace daemon — works on systems without modern kernels.',
          'Configurable crypto (AES-256-GCM by default) and transport (UDP or TCP-443 to mimic HTTPS).',
          'Big ecosystem — every router firmware, every legacy NAS, every old IoT device speaks it.',
          'TCP-443 mode useful in restrictive networks where only HTTPS is allowed out.',
          'Slower than WireGuard (15–30% throughput loss vs the modern protocol).',
        ],
        bestFor: 'Legacy hardware, routers without WireGuard support, environments that allow only TCP-443.',
      },
      {
        key: 'amnezia',
        name: 'AmneziaVPN',
        badge: 'Anti-censorship',
        tagline: 'A WireGuard variant designed to slip past deep-packet-inspection firewalls.',
        bullets: [
          'Forked from WireGuard with anti-fingerprinting tweaks at the protocol layer.',
          'Specifically modified to defeat protocol classifiers used in countries with VPN crackdowns.',
          'Same speed and CPU profile as WireGuard.',
          'Uses the official Amnezia client (iOS, Android, macOS, Windows, Linux).',
          'No promise of working everywhere — censorship engines evolve. We follow upstream.',
        ],
        bestFor: 'When standard WireGuard handshake gets blocked by a national firewall.',
      },
      {
        key: 'xray',
        name: 'XRay VLESS',
        badge: 'Stealth mode',
        tagline: 'A protocol that looks like ordinary HTTPS to anyone watching the wire.',
        bullets: [
          'Built on top of TLS 1.3 + REALITY — your traffic is indistinguishable from a real website handshake.',
          'No "VPN signature" for DPI to detect — looks like Cloudflare or any other CDN connection.',
          'Resistant to active probing (the inspector trying to handshake your endpoint and seeing what comes back).',
          'Slightly higher overhead than WireGuard but lower than OpenVPN.',
          'Client setup is fiddlier — for users who already know what XRay is.',
        ],
        bestFor: 'Hostile networks where any non-HTTPS traffic gets reset.',
      },
    ],

    // Plans
    planTitle: 'Plans and pricing',
    planSub: 'Same VPN service, three sizes — pick by how many devices you need to connect at once. All plans include unlimited traffic and a dedicated IP.',
    plans: [
      {
        intname: 'vpn_solo',
        name: 'VPN Solo',
        devices: 1,
        priceEUR: 3.99,
        bestFor: 'Personal use — phone or laptop',
      },
      {
        intname: 'vpn_family',
        name: 'VPN Family',
        devices: 5,
        priceEUR: 6.99,
        bestFor: 'Household — up to 5 devices',
        popular: true,
      },
      {
        intname: 'vpn_office',
        name: 'VPN Office',
        devices: 25,
        priceEUR: 14.99,
        bestFor: 'Small office / team — up to 25 devices',
      },
    ],
    planFeaturesTitle: 'Every plan includes',
    planFeatures: [
      { icon: 'Globe',     text: 'Choice of 13 locations worldwide' },
      { icon: 'Shield',    text: 'Dedicated IP — yours and only yours' },
      { icon: 'Wifi',      text: 'Unlimited traffic, 1 Gbps shared port' },
      { icon: 'KeyRound',  text: 'WireGuard config + QR code in 60 seconds' },
      { icon: 'Lock',      text: 'No traffic inspection or logging by us' },
      { icon: 'Award',     text: 'Cancel anytime, EU-hosted billing' },
    ],
    planCta: 'Get this plan',

    // Final CTA
    finalTitle: 'Ready to run your own VPN?',
    finalSub: 'Sign up, deploy in 60 seconds, scan the QR code, you\'re connected.',
    finalCta: 'Sign up & deploy',
    finalLogin: 'Already a client? Sign in',
  },
  ru: {
    eyebrow: 'Персональный VPN — ваш IP, ваши правила',
    h1: 'Приватный VPN, которым вы реально владеете',
    h1Sub: 'WireGuard, OpenVPN, AmneziaVPN и XRay VLESS — на выделенном IP, которым кроме вас никто не пользуется.',
    cta: 'Заказать VPN',
    cta2: 'Посмотреть тарифы',
    statUptime: 'SLA по uptime',
    statTraffic: 'Безлимитный трафик',
    statIP: 'Выделенный IP',
    statLocations: '13 локаций',

    whyTitle: 'Почему свой VPN лучше коммерческого',
    whyLead: 'Коммерческие VPN-провайдеры (NordVPN, CyberGhost, PureVPN, ProtonVPN, Surfshark и остальные) продают доступ к общему пулу — тысячи пользователей за одной горсткой IP-адресов. Это работает для «спрятать мой город от этого сайта», но имеет компромиссы, о которых в рекламе не пишут.',
    whyTable: [
      ['Ваш IP-адрес', 'Общий с тысячами других — везде помечен как «VPN»', 'Только ваш — выглядит как обычный residential или датацентровый IP'],
      ['Репутация IP', 'Заранее испорчена абузами случайных пользователей, часто заблокирован Netflix, банками, капчами', 'Чистая — пользуетесь только вы'],
      ['Приватность от провайдера', '«No-logs» обещания, которым приходится верить (и которые ломались)', 'Сервер ваш. Мы видим только метаданные подключений, не контент'],
      ['Конфигурация', 'Заперто в их приложении. Нельзя добавить peer, нельзя поменять маршрутизацию.', 'Стандартный WireGuard / OpenVPN / etc. Ваш .conf-файл. Любой клиент.'],
      ['Количество устройств', 'Жёсткий лимит по тарифу', 'До 25 устройств на тарифе Office, можно расширять'],
      ['Использование как bastion / проброс портов', 'Практически никогда', 'Да — это ваш собственный сервер'],
    ],
    whyVendors: ['NordVPN', 'CyberGhost', 'PureVPN', 'ProtonVPN', 'Surfshark', 'ExpressVPN'],

    locTitle: 'Доступно во всех наших локациях',
    locSub: 'Выберите страну, из которой должен «выходить» ваш трафик. Та же сеть, та же скорость, тот же uptime, что и у наших VPS-тарифов.',

    protoTitle: 'Выберите свой протокол VPN',
    protoSub: 'WireGuard — правильный ответ в 95% случаев: быстрый, современный, в ядре Linux. Остальные протоколы — на случай, когда они нужны.',
    protocols: [
      {
        key: 'wireguard',
        name: 'WireGuard',
        badge: 'По умолчанию • Рекомендуем',
        tagline: 'Современный VPN-протокол — быстрый, лёгкий, в ядре Linux.',
        bullets: [
          'Крошечная кодовая база (~4 000 строк против ~600 000 у OpenVPN), формально проанализирован.',
          'В mainline-ядре Linux с 5.6 — никакого userspace-демона, никакого overhead.',
          'На одном железе: 800–950 Мбит/с пропускная способность против 150–300 Мбит/с у OpenVPN.',
          'Переподключение <100 мс — рассчитан на переключение Wi-Fi → мобильный интернет.',
          'Конфиг помещается в QR-код — отсканировал в приложении и подключён.',
          'Криптография: ChaCha20-Poly1305 + Curve25519, без вариантов, в которых можно ошибиться.',
        ],
        bestFor: 'Ежедневное использование на телефоне, ноутбуке, роутере. Дефолтный выбор.',
      },
      {
        key: 'openvpn',
        name: 'OpenVPN',
        badge: 'Legacy-поддержка',
        tagline: 'Классический, широко совместимый VPN. Медленнее WireGuard, но поддерживается всем, что выпущено с 2005 года.',
        bullets: [
          'Userspace-демон — работает на системах без современных ядер.',
          'Настраиваемая криптография (AES-256-GCM по умолчанию) и транспорт (UDP или TCP-443 для мимикрии под HTTPS).',
          'Большая экосистема — любая прошивка роутера, любой старый NAS, любое старое IoT-устройство его умеет.',
          'TCP-443 режим полезен в рестриктивных сетях, где разрешён только HTTPS наружу.',
          'Медленнее WireGuard (15–30% потерь скорости).',
        ],
        bestFor: 'Старое железо, роутеры без поддержки WireGuard, среды где разрешён только TCP-443.',
      },
      {
        key: 'amnezia',
        name: 'AmneziaVPN',
        badge: 'Обход блокировок',
        tagline: 'Вариант WireGuard, разработанный для прохождения через DPI-фаерволы.',
        bullets: [
          'Форк WireGuard с антифингерпринт-доработками на уровне протокола.',
          'Специально модифицирован для прохождения протокол-классификаторов в странах с активным блокированием VPN.',
          'Та же скорость и CPU-профиль, что и у WireGuard.',
          'Использует официальный клиент Amnezia (iOS, Android, macOS, Windows, Linux).',
          'Никаких гарантий что будет работать везде — engines цензуры эволюционируют, мы следим за upstream.',
        ],
        bestFor: 'Когда стандартный WireGuard handshake блокируется национальным фаерволом.',
      },
      {
        key: 'xray',
        name: 'XRay VLESS',
        badge: 'Stealth-режим',
        tagline: 'Протокол, который для смотрящего в провод выглядит как обычный HTTPS.',
        bullets: [
          'Поверх TLS 1.3 + REALITY — ваш трафик неотличим от настоящего TLS-handshake к сайту.',
          'Никакой «VPN-сигнатуры» для DPI — выглядит как Cloudflare или любая CDN-сессия.',
          'Устойчив к active probing (когда инспектор сам делает handshake к вашему endpoint и смотрит ответ).',
          'Чуть больший overhead чем у WireGuard, но меньше OpenVPN.',
          'Настройка клиента сложнее — для пользователей, которые уже знают, что такое XRay.',
        ],
        bestFor: 'Враждебные сети, где сбрасывается любой не-HTTPS трафик.',
      },
    ],

    planTitle: 'Тарифы и цены',
    planSub: 'Один и тот же VPN-сервис в трёх размерах — выбираете по тому, сколько устройств одновременно вам нужно. Во всех тарифах безлимитный трафик и выделенный IP.',
    plans: [
      {
        intname: 'vpn_solo',
        name: 'VPN Solo',
        devices: 1,
        priceEUR: 3.99,
        bestFor: 'Личное использование — телефон или ноутбук',
      },
      {
        intname: 'vpn_family',
        name: 'VPN Family',
        devices: 5,
        priceEUR: 6.99,
        bestFor: 'Семья — до 5 устройств',
        popular: true,
      },
      {
        intname: 'vpn_office',
        name: 'VPN Office',
        devices: 25,
        priceEUR: 14.99,
        bestFor: 'Маленький офис / команда — до 25 устройств',
      },
    ],
    planFeaturesTitle: 'В каждом тарифе',
    planFeatures: [
      { icon: 'Globe',     text: 'Выбор из 13 локаций по всему миру' },
      { icon: 'Shield',    text: 'Выделенный IP — только ваш и больше ничей' },
      { icon: 'Wifi',      text: 'Безлимитный трафик, порт 1 Гбит/с' },
      { icon: 'KeyRound',  text: 'WireGuard-конфиг + QR-код за 60 секунд' },
      { icon: 'Lock',      text: 'Никакой инспекции трафика и логов с нашей стороны' },
      { icon: 'Award',     text: 'Отмена в любой момент, биллинг в ЕС' },
    ],
    planCta: 'Выбрать тариф',

    finalTitle: 'Готовы поднять свой VPN?',
    finalSub: 'Регистрация, развёртывание за 60 секунд, сканируете QR-код — вы подключены.',
    finalCta: 'Зарегистрироваться и развернуть',
    finalLogin: 'Уже клиент? Войти',
  },
};

const FEATURE_ICONS = { Globe, Shield, Wifi, KeyRound, Lock, Award };

// Map mock-data country names (EN + RU) to their VPS country page route.
// Same map as LocationsBar — keep them in sync if locations change.
const COUNTRY_ROUTES = {
  'Netherlands': '/vps-netherlands',
  'Нидерланды': '/vps-netherlands',
  'Germany': '/vps-germany',
  'Германия': '/vps-germany',
  'Poland': '/vps-poland',
  'Польша': '/vps-poland',
  'Latvia': '/vps-latvia',
  'Латвия': '/vps-latvia',
  'Czech Republic': '/vps-czechia',
  'Чехия': '/vps-czechia',
  'Italy': '/vps-italy',
  'Италия': '/vps-italy',
  'Switzerland': '/vps-switzerland',
  'Швейцария': '/vps-switzerland',
  'USA': '/vps-miami',
  'США': '/vps-miami',
  'Bulgaria': '/vps-bulgaria',
  'Болгария': '/vps-bulgaria',
  'Greece': '/vps-greece',
  'Греция': '/vps-greece',
  'Spain': '/vps-spain',
  'Испания': '/vps-spain',
  'Ukraine': '/vps-ukraine',
  'Украина': '/vps-ukraine',
  'Romania': '/vps-romania',
  'Румыния': '/vps-romania',
};

export const VPNPage = () => {
  const { language } = useLanguage();
  const ru = language === 'ru';
  const c = COPY[ru ? 'ru' : 'en'];
  const [activeProto, setActiveProto] = useState(0);
  const [visibleStats, setVisibleStats] = useState(false);
  const statsRef = useRef(null);

  const getLocalizedPath = (path) => (ru ? '/ru' + path : path);
  const countryRoutes = COUNTRY_ROUTES;

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleStats(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  const proto = c.protocols[activeProto];

  return (
    <div className="min-h-screen bg-white">
      <SEO page="vpn" />
      <Header />

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
          <div className="absolute inset-0 opacity-15">
            <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-blue-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/3 left-1/3 w-72 h-72 bg-indigo-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-semibold mb-6">
            <Lock className="w-4 h-4" />
            <span>{c.eyebrow}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            {c.h1}
          </h1>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-2xl mx-auto">
            {c.h1Sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => openBilling('register', language)} className="bg-white text-blue-950 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-2xl">
              {c.cta}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={scrollToPricing} className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl">
              {c.cta2}
            </Button>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────── */}
      <section ref={statsRef} className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Gauge,  value: '99.9%',  label: c.statUptime },
              { icon: Wifi,   value: '∞',       label: c.statTraffic },
              { icon: Shield, value: c.statIP,  label: ru ? 'Только ваш' : 'Yours alone' },
              { icon: Globe,  value: '13',      label: c.statLocations },
            ].map((stat, i) => (
              <div key={i} className={"text-center transition-all duration-700 " + (visibleStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4')} style={{ transitionDelay: i * 150 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-3">
                  <stat.icon className="w-7 h-7 text-blue-700" />
                </div>
                <div className="text-3xl sm:text-4xl font-black text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why us vs commercial VPNs — inverted layout with Trophy ──── */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">{c.whyTitle}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">{c.whyLead}</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* LEFT: NetGrid Host — winner column */}
            <div className="relative rounded-3xl bg-gradient-to-br from-blue-700 to-indigo-800 p-8 sm:p-10 text-white shadow-2xl shadow-blue-700/30 lg:scale-105">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight">NetGrid Host VPN</h3>
                <Trophy className="w-12 h-12 text-amber-300 flex-shrink-0" />
              </div>
              <p className="text-blue-100 text-base mb-7">{ru ? 'Что есть у нас' : 'What you get'}</p>
              <ul className="space-y-4">
                {c.whyTable.map(([cat, _theirs, ours], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-emerald-900" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-white">{cat}</div>
                      <div className="text-sm text-blue-100/90 leading-relaxed">{ours}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {/* RIGHT: Commercial VPNs — what's missing */}
            <div className="relative rounded-3xl bg-white border-2 border-slate-200 p-8 sm:p-10 lg:mt-4">
              <div className="mb-2">
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mb-2">
                  {ru ? 'Коммерческие VPN' : 'Commercial VPNs'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {c.whyVendors.map((v) => (
                    <span key={v} className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-xs font-medium">{v}</span>
                  ))}
                </div>
              </div>
              <p className="text-slate-500 text-base mt-3 mb-7">{ru ? 'Чего вы у них не получите' : "What you won't get there"}</p>
              <ul className="space-y-4">
                {c.whyTable.map(([cat, theirs], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 text-slate-400 font-bold text-sm">—</div>
                    <div className="min-w-0">
                      <div className="font-bold text-slate-700">{cat}</div>
                      <div className="text-sm text-slate-500 leading-relaxed">{theirs}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Locations ──────────────────────────────────────── */
      /* Same look-and-feel as the home page LocationsBar so VPN locations
         feel like a first-class citizen of the same product family. Card
         design (flag + country + MapPin city + ArrowUpRight) is copied
         intentionally — do not redesign in isolation. Routes go to the
         matching VPS country page so visitors can also see what raw
         hardware is in that DC. */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3 tracking-tight">{c.locTitle}</h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">{c.locSub}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
            {(ru ? locationsRu : locationsEn).map((location, index) => {
              const route = countryRoutes[location.country];
              const card = (
                <div className="group relative h-full p-5 bg-white rounded-2xl border border-slate-200 hover:border-blue-500 hover:shadow-md hover:shadow-blue-700/10 hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <img
                      src={location.flag}
                      alt=""
                      loading="lazy"
                      className="w-10 h-7 object-cover rounded-md ring-1 ring-slate-200/60"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {location.country}
                      </div>
                      <div className="flex items-center gap-1 text-slate-500 text-sm">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{location.city}</span>
                      </div>
                    </div>
                    {!location.comingSoon && (
                      <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-blue-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                    )}
                  </div>
                </div>
              );

              if (location.comingSoon || !route) {
                return (
                  <div key={index} className="opacity-60 cursor-default h-full">
                    {card}
                  </div>
                );
              }

              return (
                <Link key={index} to={getLocalizedPath(route)} className="block h-full">
                  {card}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Protocols (tabs) ───────────────────────────────── */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">{c.protoTitle}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{c.protoSub}</p>
          </div>
          {/* Segmented control — single connected pill bar with the
              active option visually fused in. Same behaviour as before
              (one selectable tab) but reads as one control instead of a
              row of independent buttons. Scroll horizontally on small
              screens so all four protocols stay reachable without
              wrapping into two rows. */}
          <div className="flex justify-center mb-8">
            <div
              role="tablist"
              aria-label={ru ? 'Протокол VPN' : 'VPN protocol'}
              className="inline-flex p-1 rounded-2xl bg-slate-100 border border-slate-200 shadow-inner overflow-x-auto max-w-full"
            >
              {c.protocols.map((p, i) => {
                const active = i === activeProto;
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveProto(i)}
                    className={
                      'px-5 sm:px-6 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ' +
                      (active
                        ? 'bg-white text-blue-700 shadow-md shadow-slate-900/5'
                        : 'bg-transparent text-slate-600 hover:text-slate-900')
                    }
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Tab body */}
          <div className="rounded-3xl bg-white border-2 border-blue-100 shadow-lg p-8 sm:p-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h3 className="text-3xl font-black text-slate-900">{proto.name}</h3>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-wider">
                {proto.badge}
              </span>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{proto.tagline}</p>
            <ul className="space-y-3 mb-6">
              {proto.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
              <div className="text-xs uppercase tracking-wider font-bold text-blue-700 mb-1">{ru ? 'Лучше всего для' : 'Best for'}</div>
              <div className="text-sm text-blue-900 font-medium">{proto.bestFor}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────── */}
      <section id="pricing" className="relative py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">{c.planTitle}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{c.planSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {c.plans.map((p) => (
              <div
                key={p.intname}
                className={
                  'relative rounded-2xl p-6 transition-all ' +
                  (p.popular
                    ? 'bg-gradient-to-br from-blue-700 to-indigo-800 text-white shadow-2xl scale-105 border-2 border-blue-700'
                    : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg')
                }
              >
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-xs font-bold uppercase tracking-wider shadow-md">
                    {ru ? 'Популярный' : 'Popular'}
                  </div>
                )}
                <div className={"text-xs font-semibold uppercase tracking-widest mb-2 " + (p.popular ? 'text-blue-200' : 'text-blue-700')}>
                  {p.name}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className={"text-2xl font-semibold " + (p.popular ? 'text-blue-200' : 'text-blue-700')}>€</span>
                  <span className={"text-5xl font-black tracking-tight leading-none " + (p.popular ? 'text-white' : 'text-slate-900')}>
                    {Math.floor(p.priceEUR)}
                  </span>
                  {p.priceEUR % 1 !== 0 && (
                    <span className={"text-2xl font-bold tracking-tight leading-none -ml-0.5 " + (p.popular ? 'text-white' : 'text-slate-900')}>
                      .{String(p.priceEUR.toFixed(2)).split(".")[1]}
                    </span>
                  )}
                  <span className={"text-sm ml-1 " + (p.popular ? 'text-blue-200' : 'text-slate-500')}>
                    /{ru ? 'мес' : 'mo'}
                  </span>
                </div>
                <div className={"text-sm mb-5 " + (p.popular ? 'text-blue-100' : 'text-slate-600')}>{p.bestFor}</div>
                <div className="flex items-center gap-2 mb-5">
                  <Smartphone className={"w-4 h-4 " + (p.popular ? 'text-blue-200' : 'text-blue-700')} />
                  <span className={"text-sm font-semibold " + (p.popular ? 'text-white' : 'text-slate-900')}>
                    {ru ? 'до ' : 'up to '}{p.devices} {ru ? (p.devices === 1 ? 'устройство' : 'устройств') : (p.devices === 1 ? 'device' : 'devices')}
                  </span>
                </div>
                <Button
                  onClick={() => openBilling('register', language)}
                  className={
                    'w-full font-bold rounded-xl py-3 ' +
                    (p.popular
                      ? 'bg-white text-blue-700 hover:bg-blue-50'
                      : 'bg-blue-700 text-white hover:bg-blue-800')
                  }
                >
                  {c.planCta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-5">{c.planFeaturesTitle}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.planFeatures.map((f, i) => {
                const Ic = FEATURE_ICONS[f.icon] || CheckCircle;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Ic className="w-4 h-4 text-blue-700" />
                    </div>
                    <span className="text-sm text-slate-700 leading-snug">{f.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Payment methods sit right after pricing — visitors decide on
          a tariff, then immediately see how they can pay for it. The
          previous layout had Final CTA in between which forced a
          re-scroll up to confirm "wait, do they take crypto?". */}
      <PaymentMethodsSection transparentBg={false} />

      {/* ── Final CTA ──────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl sm:text-5xl font-black mb-4">{c.finalTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">{c.finalSub}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => openBilling('register', language)} className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-2xl">
              {c.finalCta}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => openBilling('logon', language)} className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl">
              {c.finalLogin}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VPNPage;
